import { getSubjectLabels } from '@/lib/settings';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { personTypesApi, type PersonType } from '../../lib/person-types';
import { useGeoapifyAddressAutocomplete } from '../../hooks/useGeoapifyAddressAutocomplete';
import SubProfileForm from './SubProfileForm';
import ConfirmWizardModal from '../ui/ConfirmWizardModal';
import { Textarea } from '../ui/textarea';
import LoeschenMitGrundDialog, { grundHeader } from '../ui/loeschen-mit-grund-dialog';
import { usePermissions } from '../rbac/use-permission';
import { P } from '../../lib/rbac/permissions.generated';

type Person = {
  id: string;
  name: string;
  email: string;
  phone?: string;
  type: 'student' | 'teacher' | 'both' | 'customer';
  status: 'active' | 'inactive' | 'pending';
  instrument?: string;
  joinDate: string;
  birthDate?: string;
  address?: string;
  notes?: string;
  wpUserId?: string;
  parentId?: string;
  subProfiles?: string[];
  roles?: string[];
  // Account-/Zugangsfelder (aus GET /api/personen/:id):
  account_id?: string | null;
  hasAccount?: boolean;
  login?: string | null;
  accessLocked?: boolean;
  salutation?: 'Herr' | 'Frau' | 'Divers' | 'Firma';
  newsletter?: boolean;
  structuredAddress?: any;
  communications?: { type: string; value: string; officeContactAllowed?: boolean; isPrimary?: boolean }[];
  communicationChannels?: { type: string; value: string; officeContactAllowed?: boolean; isPrimary?: boolean }[];
  teacher?: {
    shortCode?: string | null;
    taxNumber?: string | null;
    bank?: { accountHolder?: string | null; iban?: string | null } | null;
    subjects?: number[] | null;
  };
};

type TabKey = 'stammdaten' | 'subprofile' | 'teacher' | 'zugang';

export type PersonDetailsWizardModalProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  personId: string | null;
  initialTab?: TabKey;
  onUpdated?: (person: Person) => void;
  onDeleted?: (personId: string) => void;
};

function splitName(full: string): { first_name?: string; last_name?: string } {
  const f = String(full || '').trim();
  if (!f) return {};
  const parts = f.split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0] };
  const last = String(parts.pop() || '').trim();
  const first = parts.join(' ').trim();
  return { first_name: first || undefined, last_name: last || undefined };
}

export default function PersonDetailsWizardModal({
  open,
  onOpenChange,
  personId,
  initialTab = 'stammdaten',
  onUpdated,
  onDeleted,
}: PersonDetailsWizardModalProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<TabKey>(initialTab);
  const [person, setPerson] = useState<Person | null>(null);
  const [personTypes, setPersonTypes] = useState<PersonType[]>([]);

  const [formData, setFormData] = useState<Partial<Person>>({});

  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const { suggestions: addressSuggestionsHook } = useGeoapifyAddressAutocomplete({
    query: addressQuery,
    enabled: open,
  });
  // Unterdrückt das erneute Aufklappen der Vorschlagsliste, nachdem ein
  // Vorschlag übernommen wurde (der Hook fragt sonst für die volle Adresse neu).
  const suppressAddressSyncRef = useRef(false);

  const [subprofiles, setSubprofiles] = useState<any[]>([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subWizardOpen, setSubWizardOpen] = useState(false);
  const [editingSubIndex, setEditingSubIndex] = useState<number | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [profilZuLoeschen, setProfilZuLoeschen] = useState<number | null>(null);
  const [archiving, setArchiving] = useState(false);
  const { can } = usePermissions();
  // Entscheidung F17 (2026-09-21): Archivieren ist der Weg fürs Tagesgeschäft,
  // endgültiges Löschen bleibt der globalen Administration vorbehalten.
  const darfArchivieren = can(P.personen.personen.person_archivieren);
  const darfLoeschen = can(P.personen.personen.person_loeschen);
  const istArchiviert = person?.status === 'inactive';

  // Teacher edit state (basic meta only – advanced settings live in deep detail page)
  const [subjectOptions, setSubjectOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [teacherEditing, setTeacherEditing] = useState(false);
  const [teacherForm, setTeacherForm] = useState<{
    shortCode: string;
    taxNumber: string;
    bankAccountHolder: string;
    bankIban: string;
    subjectIds: number[];
  }>({ shortCode: '', taxNumber: '', bankAccountHolder: '', bankIban: '', subjectIds: [] });

  // Zugang/Account (Login, Passwort, Sperre)
  const [newPassword, setNewPassword] = useState('');
  const [accessBusy, setAccessBusy] = useState(false);

  useEffect(() => {
    // Nach Auswahl eines Vorschlags NICHT erneut öffnen — sonst klappt die
    // Liste sofort wieder auf, weil der Hook für die übernommene Adresse
    // ein neues Ergebnis liefert.
    if (suppressAddressSyncRef.current) {
      suppressAddressSyncRef.current = false;
      setAddressSuggestions([]);
      return;
    }
    setAddressSuggestions(addressSuggestionsHook as any[]);
  }, [addressSuggestionsHook]);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    if (!personId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [types, p] = await Promise.all([
          personTypesApi.list(),
          api.get<Person>(`/api/personen/${personId}`),
        ]);
        if (cancelled) return;
        setPersonTypes((types || []).filter((t) => t.aktiv));
        setPerson(p);
        const initialChannels = (p as any).communicationChannels || (p as any).communications || (p.email ? [{ type: 'email', value: p.email, isPrimary: true, officeContactAllowed: true }] : []);
        setFormData({
          ...p,
          communicationChannels: initialChannels,
        });
        setAddressQuery(String((p as any).address || ''));

        // teacher form init
        const t = (p as any).teacher || {};
        setTeacherForm({
          shortCode: String(t.shortCode || ''),
          taxNumber: String(t.taxNumber || ''),
          bankAccountHolder: String((t.bank && t.bank.accountHolder) || ''),
          bankIban: String((t.bank && t.bank.iban) || ''),
          subjectIds: Array.isArray(t.subjects) ? (t.subjects as any[]).map((n: any) => Number(n)) : [],
        });
        setTeacherEditing(false);
      } catch (e: any) {
        toast.error(e?.message || 'Person konnte nicht geladen werden');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, personId]);

  useEffect(() => {
    if (!open) return;
    if (!personId) return;
    let cancelled = false;
    const loadSubs = async () => {
      setSubLoading(true);
      try {
        const list = await api.get<any[]>(`/api/personen/${personId}/subprofiles`);
        if (cancelled) return;
        setSubprofiles(Array.isArray(list) ? list.filter((sp) => Number(sp?.index || 0) > 1) : []);
      } catch {
        if (!cancelled) setSubprofiles([]);
      } finally {
        if (!cancelled) setSubLoading(false);
      }
    };
    loadSubs();
    return () => {
      cancelled = true;
    };
  }, [open, personId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadSubjects = async () => {
      try {
        const faecher = await api.get<Array<any>>('/api/faecher');
        if (cancelled) return;
        const opts = (faecher || []).map((f: any) => ({ id: Number(f.id), name: String(f.name) }));
        setSubjectOptions(opts);
      } catch {
        if (!cancelled) setSubjectOptions([]);
      }
    };
    loadSubjects();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const typeLabel = useMemo(() => {
    const code = String(formData.type || person?.type || '').trim();
    const match = personTypes.find((pt) => pt.code === code);
    return match?.name || code;
  }, [formData.type, person?.type, personTypes]);

  const isTeacher = useMemo(() => {
    const roles = Array.isArray(person?.roles) ? (person?.roles as any[]) : [];
    return roles.includes('teacher') || person?.type === 'teacher' || person?.type === 'both';
  }, [person?.roles, person?.type]);

  const canSave = useMemo(() => {
    if (!person) return false;
    const name = String(formData.name || '').trim();
    const channels = Array.isArray((formData as any).communicationChannels)
      ? (formData as any).communicationChannels
      : [];
    const hasEmail = channels.some((c: any) => c?.type === 'email' && String(c?.value || '').trim() !== '');
    return name.length > 0 && hasEmail && !saving && !loading;
  }, [formData.name, (formData as any).communicationChannels, loading, person, saving]);

  const handleSave = async () => {
    if (!person) return;
    const name = String(formData.name || '').trim();
    const channels = Array.isArray((formData as any).communicationChannels)
      ? (formData as any).communicationChannels
      : [];
    const primaryEmail = channels.find((c: any) => c?.type === 'email' && c?.isPrimary)?.value
      || channels.find((c: any) => c?.type === 'email')?.value
      || '';
    if (!name || !String(primaryEmail).trim()) {
      toast.error('Bitte Name und mindestens eine E-Mail als Kommunikationsweg ausfüllen');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        email: primaryEmail,
        display_name: name,
        salutation: (formData.salutation || undefined) as any,
        newsletter: formData.newsletter === true,
        birthDate: (formData.birthDate || undefined) as any,
        communicationChannels: channels as any,
      };
      const nameParts = splitName(name);
      if (nameParts.first_name) payload.first_name = nameParts.first_name;
      if (nameParts.last_name) payload.last_name = nameParts.last_name;

      if ((formData as any).structuredAddress) {
        const structured = (formData as any).structuredAddress || {};
        const zip = String(structured.zip || structured.postalCode || '').trim();
        payload.structuredAddress = {
          ...structured,
          zip,
          postalCode: zip,
        };
      }
      else if (String(formData.address || '').trim()) payload.address = String(formData.address || '').trim();

      const updated = await api.patch<Person>(`/api/personen/${person.wpUserId || person.id}`, payload);
      setPerson(updated);
      setFormData({ ...updated, communicationChannels: (updated as any).communicationChannels || (updated as any).communications || [] });
      setAddressQuery(String((updated as any).address || ''));
      onUpdated?.(updated);
      toast.success('Person gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleStructuredAddressField = (field: 'street' | 'zip' | 'city', value: string) => {
    setFormData((prev) => {
      const current = (prev as any).structuredAddress || {};
      const next: any = { ...current, [field]: value };
      if (field === 'zip') next.postalCode = value;
      const line1 = `${next.street || ''} ${next.houseNumber || ''}`.trim();
      const line2 = `${next.zip || next.postalCode || ''} ${next.city || ''}`.trim();
      next.formatted = [line1, line2].filter(Boolean).join(', ');
      return { ...prev, structuredAddress: next, address: next.formatted || prev.address };
    });
  };

  const openAddSubprofile = () => {
    setEditingSubIndex(null);
    setSubWizardOpen(true);
  };
  const openEditSubprofile = (index: number) => {
    setEditingSubIndex(index);
    setSubWizardOpen(true);
  };

  const saveSubprofile = async (data: any) => {
    if (!person) return;
    try {
      if (editingSubIndex === null) {
        const created = await api.post<any>(`/api/personen/${person.wpUserId || person.id}/subprofiles`, data);
        setSubprofiles((prev) => [...prev, created]);
        toast.success('Schülerprofil hinzugefügt');
      } else {
        const sp = subprofiles[editingSubIndex];
        const updated = await api.put<any>(`/api/personen/${person.wpUserId || person.id}/subprofiles/${sp.id}`, data);
        setSubprofiles((prev) => prev.map((it, idx) => (idx === editingSubIndex ? updated : it)));
        toast.success('Schülerprofil aktualisiert');
      }
    } catch {
      toast.error('Schülerprofil speichern fehlgeschlagen');
    } finally {
      setSubWizardOpen(false);
      setEditingSubIndex(null);
    }
  };

  const deleteSubprofile = (index: number) => {
    if (!person) return;
    const sp = subprofiles[index];
    if (Number(sp?.index || 0) === 1) {
      toast.error('Das Hauptprofil kann nicht gelöscht werden');
      return;
    }
    setProfilZuLoeschen(index);
  };

  const subprofilLoeschen = async (index: number, grund: string) => {
    if (!person) return;
    const sp = subprofiles[index];
    await api.delete(`/api/personen/${person.wpUserId || person.id}/subprofiles/${sp.id}`, grundHeader(grund));
    setSubprofiles((prev) => prev.filter((_, idx) => idx !== index));
    setProfilZuLoeschen(null);
    toast.success('Schülerprofil gelöscht');
  };

  const handleDeletePerson = async () => {
    if (!person) return;
    const grund = deleteReason.trim();
    if (!grund) {
      toast.error('Bitte eine Begründung angeben');
      return;
    }
    try {
      // Endgültiges Löschen wird protokolliert und verlangt eine Begründung (D.8).
      await api.delete(`/api/personen/${person.wpUserId || person.id}`, {
        headers: { 'X-Sidigo-Reason': grund },
      });
      toast.success('Person gelöscht');
      onDeleted?.(String(person.id));
      setDeleteOpen(false);
      setDeleteReason('');
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Löschen fehlgeschlagen');
    }
  };

  const handleArchivePerson = async () => {
    if (!person) return;
    const archivieren = !istArchiviert;
    setArchiving(true);
    try {
      await api.post(`/api/personen/${person.wpUserId || person.id}/archivieren`, {
        archiviert: archivieren,
      });
      const updated: Person = { ...person, status: archivieren ? 'inactive' : 'active' };
      setPerson(updated);
      onUpdated?.(updated);
      toast.success(archivieren ? 'Person archiviert' : 'Person zurückgeholt');
    } catch (e: any) {
      toast.error(e?.message || (archivieren ? 'Archivieren fehlgeschlagen' : 'Zurückholen fehlgeschlagen'));
    } finally {
      setArchiving(false);
    }
  };

  const saveTeacher = async () => {
    if (!person) return;
    if (teacherForm.subjectIds.length === 0) {
      toast.error(`Bitte mindestens 1 ${getSubjectLabels().singular} auswählen`);
      return;
    }
    if (teacherForm.shortCode.trim() === '') {
      toast.error('Bitte ein Kürzel angeben (z. B. „HaMü“)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        shortCode: teacherForm.shortCode || undefined,
        taxNumber: teacherForm.taxNumber || undefined,
        bank: { accountHolder: teacherForm.bankAccountHolder || '', iban: teacherForm.bankIban || '' },
        subjects: teacherForm.subjectIds,
      } as any;

      let updated: Person;
      if (!isTeacher) {
        // promotion also writes teacher meta
        const res = await api.post<{ message?: string; person: Person }>(`/api/personen/${person.wpUserId || person.id}/promote-teacher`, payload);
        updated = (res as any)?.person || (res as any);
      } else {
        updated = await api.put<Person>(`/api/personen/${person.wpUserId || person.id}/teacher`, payload);
      }

      setPerson(updated);
      setTeacherEditing(false);
      const t = (updated as any).teacher || {};
      setTeacherForm({
        shortCode: String(t.shortCode || ''),
        taxNumber: String(t.taxNumber || ''),
        bankAccountHolder: String((t.bank && t.bank.accountHolder) || ''),
        bankIban: String((t.bank && t.bank.iban) || ''),
        subjectIds: Array.isArray(t.subjects) ? (t.subjects as any[]).map((n: any) => Number(n)) : [],
      });
      onUpdated?.(updated);
      toast.success(!isTeacher ? 'Als Lehrer gespeichert' : 'Lehrer-Daten gespeichert');
    } catch (e: any) {
      toast.error(e?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const hasAccount = useMemo(() => {
    if (!person) return false;
    return Boolean((person as any).hasAccount ?? person.account_id ?? person.login);
  }, [person]);

  const refreshPerson = async () => {
    if (!person) return;
    try {
      const p = await api.get<Person>(`/api/personen/${person.wpUserId || person.id}`);
      setPerson(p);
      onUpdated?.(p);
    } catch {
      /* Status wird beim nächsten Öffnen ohnehin neu geladen */
    }
  };

  const handleToggleAccess = async (lock: boolean) => {
    if (!person) return;
    setAccessBusy(true);
    try {
      const path = lock ? 'access/lock' : 'access/unlock';
      await api.post(`/api/personen/${person.wpUserId || person.id}/${path}`, {});
      await refreshPerson();
      toast.success(lock ? 'Zugang gesperrt' : 'Zugang freigeschaltet');
    } catch (e: any) {
      toast.error(e?.message || 'Zugang konnte nicht geändert werden');
    } finally {
      setAccessBusy(false);
    }
  };

  const handleSetPassword = async () => {
    if (!person) return;
    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    setAccessBusy(true);
    try {
      await api.post(`/api/personen/${person.wpUserId || person.id}/password`, { new_password: newPassword });
      setNewPassword('');
      toast.success('Passwort gesetzt');
    } catch (e: any) {
      toast.error(e?.message || 'Passwort konnte nicht gesetzt werden');
    } finally {
      setAccessBusy(false);
    }
  };

  const handleResendOnboarding = async () => {
    if (!person) return;
    setAccessBusy(true);
    try {
      const res = await api.post<{ ok?: boolean; error?: string }>(`/api/personen/${person.wpUserId || person.id}/onboarding/resend`, {});
      if (res?.ok === false) {
        toast.error(res?.error || 'Registrierungsmail konnte nicht gesendet werden');
      } else {
        toast.success('Onboarding-/Willkommensmail versendet');
      }
    } catch (e: any) {
      toast.error(e?.message || 'Registrierungsmail konnte nicht gesendet werden');
    } finally {
      setAccessBusy(false);
    }
  };

  const title = (
    <div className="flex flex-col gap-1">
      <div className="text-base font-semibold">Person</div>
      <div className="text-sm text-muted-foreground">
        {(person?.name || '').trim() || '—'} · {typeLabel || '—'} · ID {personId || '—'}
      </div>
    </div>
  );

  return (
    <>
      <WizardShell
        open={open}
        onOpenChange={(v) => {
          onOpenChange(v);
          if (!v) {
            setDeleteOpen(false);
            setSubWizardOpen(false);
            setEditingSubIndex(null);
          }
        }}
        title={title}
        description="Stammdaten, Schülerprofile und Lehrer-Optionen bearbeiten."
        bodyClassName="py-4 overflow-hidden"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving || loading}>
              Schließen
            </Button>
            <div className="flex items-center gap-2">
              {darfArchivieren && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleArchivePerson}
                  disabled={!person || saving || loading || archiving}
                >
                  {istArchiviert ? 'Zurückholen' : 'Archivieren'}
                </Button>
              )}
              {darfLoeschen && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDeleteOpen(true)}
                  disabled={!person || saving || loading}
                >
                  Löschen
                </Button>
              )}
              <Button type="button" onClick={handleSave} disabled={!canSave}>
                Speichern
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex h-full min-h-0 flex-col gap-4">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)} className="flex-1 min-h-0 flex flex-col">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="stammdaten">Stammdaten</TabsTrigger>
              <TabsTrigger value="subprofile">Schülerprofile</TabsTrigger>
              <TabsTrigger value="teacher">Lehrer</TabsTrigger>
              <TabsTrigger value="zugang">Zugang</TabsTrigger>
            </TabsList>

            <TabsContent value="stammdaten" className="flex-1 min-h-0 overflow-y-auto pt-4 space-y-4">
              {!person ? (
                <div className="text-sm text-muted-foreground">{loading ? 'Lade…' : 'Keine Person ausgewählt'}</div>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Name</label>
                      <Input value={String(formData.name || '')} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium">Kommunikationswege</label>
                      <div className="space-y-2 rounded-md border p-3">
                        {((formData as any).communicationChannels || []).map((channel: any, idx: number) => (
                          <div key={`channel-${idx}`} className="grid gap-2 md:grid-cols-12">
                            <Select
                              value={String(channel.type || 'email')}
                              onValueChange={(value) => setFormData((prev) => {
                                const list = [...(((prev as any).communicationChannels || []) as any[])];
                                list[idx] = { ...list[idx], type: value };
                                return { ...prev, communicationChannels: list };
                              })}
                            >
                              <SelectTrigger className="md:col-span-2">
                                <span>{String(channel.type || 'Typ')}</span>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="email">E-Mail</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="mobil">Mobil</SelectItem>
                                <SelectItem value="festnetz">Festnetz</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              className="md:col-span-6"
                              value={String(channel.value || '')}
                              onChange={(e) => setFormData((prev) => {
                                const list = [...(((prev as any).communicationChannels || []) as any[])];
                                list[idx] = { ...list[idx], value: e.target.value };
                                return { ...prev, communicationChannels: list };
                              })}
                              placeholder="Kontaktwert"
                            />
                            <label className="md:col-span-3 flex items-center gap-2 text-sm">
                              <Switch
                                checked={channel.officeContactAllowed !== false}
                                onCheckedChange={(checked) => setFormData((prev) => {
                                  const list = [...(((prev as any).communicationChannels || []) as any[])];
                                  list[idx] = { ...list[idx], officeContactAllowed: checked };
                                  return { ...prev, communicationChannels: list };
                                })}
                              />
                              Office erlaubt
                            </label>
                            <Button
                              type="button"
                              variant="outline"
                              className="md:col-span-1"
                              onClick={() => setFormData((prev) => {
                                const list = [...(((prev as any).communicationChannels || []) as any[])];
                                list.splice(idx, 1);
                                return { ...prev, communicationChannels: list };
                              })}
                            >
                              x
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setFormData((prev) => ({
                            ...prev,
                            communicationChannels: [...(((prev as any).communicationChannels || []) as any[]), { type: 'email', value: '', officeContactAllowed: true }],
                          }))}
                        >
                          Kommunikationsweg hinzufügen
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Anrede</label>
                      <Select
                        value={String(formData.salutation || '')}
                        onValueChange={(v) => setFormData((p) => ({ ...p, salutation: (v || undefined) as any }))}
                      >
                        <SelectTrigger className="w-full">
                          <span>{String(formData.salutation || '') || 'Auswählen'}</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Herr">Herr</SelectItem>
                          <SelectItem value="Frau">Frau</SelectItem>
                          <SelectItem value="Divers">Divers</SelectItem>
                          <SelectItem value="Firma">Firma</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Geburtsdatum</label>
                      <Input type="date" value={String(formData.birthDate || '')} onChange={(e) => setFormData((p) => ({ ...p, birthDate: e.target.value }))} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Newsletter</label>
                      <div className="flex items-center justify-between rounded-md border p-3">
                        <div className="text-sm text-muted-foreground">E-Mail Newsletter erhalten</div>
                        <Switch checked={formData.newsletter === true} onCheckedChange={(v) => setFormData((p) => ({ ...p, newsletter: v }))} />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Adresse</label>
                    <div className="relative">
                      <Input
                        placeholder="Adresse suchen…"
                        autoComplete="street-address"
                        value={addressQuery || String(formData.address || '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          // Manuelles Tippen: Vorschläge wieder zulassen.
                          suppressAddressSyncRef.current = false;
                          setAddressQuery(v);
                          setFormData((p) => ({
                            ...p,
                            address: v,
                            structuredAddress: {
                              ...((p as any).structuredAddress || {}),
                              formatted: v,
                            },
                          }));
                        }}
                      />
                      {addressSuggestions.length > 0 ? (
                        <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow">
                          {addressSuggestions.map((s) => (
                            <button
                              key={s.id}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-muted"
                              onClick={() => {
                                const zip = String((s as any)?.structured?.zip || (s as any)?.structured?.postalCode || '').trim();
                                // Hausnummer in das Straßen-Feld übernehmen
                                // (deutsche Konvention: "Straße Hausnummer").
                                const mergedStreet = `${s.structured.street || ''} ${s.structured.houseNumber || ''}`.trim();
                                suppressAddressSyncRef.current = true;
                                setFormData((p) => ({
                                  ...p,
                                  address: s.structured.formatted,
                                  structuredAddress: {
                                    ...s.structured,
                                    street: mergedStreet,
                                    houseNumber: '',
                                    zip,
                                    postalCode: zip,
                                  },
                                }));
                                setAddressQuery(s.structured.formatted);
                                setAddressSuggestions([]);
                              }}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className="grid gap-3 md:grid-cols-3">
                      <Input
                        value={String((formData as any).structuredAddress?.street || '')}
                        onChange={(e) => handleStructuredAddressField('street', e.target.value)}
                        placeholder="Straße"
                      />
                      <Input
                        value={String((formData as any).structuredAddress?.zip || (formData as any).structuredAddress?.postalCode || '')}
                        onChange={(e) => handleStructuredAddressField('zip', e.target.value)}
                        placeholder="PLZ"
                      />
                      <Input
                        value={String((formData as any).structuredAddress?.city || '')}
                        onChange={(e) => handleStructuredAddressField('city', e.target.value)}
                        placeholder="Ort"
                      />
                    </div>
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="subprofile" className="flex-1 min-h-0 overflow-y-auto pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Schülerprofile</div>
                  <div className="text-xs text-muted-foreground">Übergeordnetes Kundenprofil</div>
                </div>
                <Button type="button" onClick={openAddSubprofile} disabled={!person}>
                  Neues Schülerprofil
                </Button>
              </div>

              {!person ? (
                <div className="text-sm text-muted-foreground">Keine Person ausgewählt.</div>
              ) : subLoading ? (
                <div className="text-sm text-muted-foreground">Lade…</div>
              ) : subprofiles.length === 0 ? (
                <div className="rounded-md border p-4 text-sm text-muted-foreground">Keine Schülerprofile vorhanden.</div>
              ) : (
                <div className="space-y-2">
                  {subprofiles.map((sp, idx) => (
                    <div key={sp.id || idx} className="rounded-md border p-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{sp.name || `Schülerprofil ${idx + 1}`}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          Index: {sp.index}
                          {Number(sp.index || 0) === 1 ? ' • Hauptprofil (nicht löschbar)' : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button type="button" variant="secondary" onClick={() => openEditSubprofile(idx)}>
                          Bearbeiten
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          onClick={() => deleteSubprofile(idx)}
                          disabled={Number(sp.index || 0) === 1}
                        >
                          Löschen
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="teacher" className="flex-1 min-h-0 overflow-y-auto pt-4 space-y-3">
              {!person ? (
                <div className="text-sm text-muted-foreground">Keine Person ausgewählt.</div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="font-medium">Lehrer</div>
                      <div className="text-xs text-muted-foreground">
                        {isTeacher ? 'Lehrer-Metadaten bearbeiten' : 'Diese Person ist noch kein Lehrer'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {teacherEditing ? (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const t = (person as any).teacher || {};
                              setTeacherForm({
                                shortCode: String(t.shortCode || ''),
                                taxNumber: String(t.taxNumber || ''),
                                bankAccountHolder: String((t.bank && t.bank.accountHolder) || ''),
                                bankIban: String((t.bank && t.bank.iban) || ''),
                                subjectIds: Array.isArray(t.subjects) ? (t.subjects as any[]).map((n: any) => Number(n)) : [],
                              });
                              setTeacherEditing(false);
                            }}
                            disabled={saving || loading}
                          >
                            Abbrechen
                          </Button>
                          <Button type="button" onClick={saveTeacher} disabled={saving || loading}>
                            Speichern
                          </Button>
                        </>
                      ) : (
                        <Button type="button" onClick={() => setTeacherEditing(true)} disabled={saving || loading}>
                          {isTeacher ? 'Bearbeiten' : 'Zum Lehrer ernennen'}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label htmlFor="wizardTeacherShort" className="text-sm font-medium">Kürzel</label>
                      {teacherEditing ? (
                        <Input id="wizardTeacherShort" value={teacherForm.shortCode} placeholder="z. B. HaMü" onChange={(e) => setTeacherForm((p) => ({ ...p, shortCode: e.target.value }))} />
                      ) : (
                        <div className="rounded-md border p-3 text-sm">{teacherForm.shortCode || '—'}</div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Steuernummer</label>
                      {teacherEditing ? (
                        <Input value={teacherForm.taxNumber} onChange={(e) => setTeacherForm((p) => ({ ...p, taxNumber: e.target.value }))} />
                      ) : (
                        <div className="rounded-md border p-3 text-sm">{teacherForm.taxNumber || '—'}</div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Kontoinhaber</label>
                      {teacherEditing ? (
                        <Input value={teacherForm.bankAccountHolder} onChange={(e) => setTeacherForm((p) => ({ ...p, bankAccountHolder: e.target.value }))} />
                      ) : (
                        <div className="rounded-md border p-3 text-sm">{teacherForm.bankAccountHolder || '—'}</div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">IBAN</label>
                      {teacherEditing ? (
                        <Input value={teacherForm.bankIban} onChange={(e) => setTeacherForm((p) => ({ ...p, bankIban: e.target.value }))} />
                      ) : (
                        <div className="rounded-md border p-3 text-sm">{teacherForm.bankIban || '—'}</div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">{getSubjectLabels().plural} (mindestens 1)</label>
                    <div className="rounded-md border p-3">
                      {subjectOptions.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Keine {getSubjectLabels().plural} geladen</div>
                      ) : (
                        <div className="grid gap-2 md:grid-cols-2">
                          {subjectOptions.map((opt) => (
                            <label key={opt.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300"
                                checked={teacherForm.subjectIds.includes(opt.id)}
                                disabled={!teacherEditing}
                                onChange={(e) => {
                                  setTeacherForm((prev) => ({
                                    ...prev,
                                    subjectIds: e.target.checked
                                      ? [...prev.subjectIds, opt.id]
                                      : prev.subjectIds.filter((id) => id !== opt.id),
                                  }));
                                }}
                              />
                              <span className="text-sm">{opt.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {!teacherEditing ? (
                      <div className="text-xs text-muted-foreground">
                        Hinweis: Für Honorar/Unterrichtsformen und Freigabezeiten bitte „Mehr Details“ auf der Detailseite nutzen.
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="zugang" className="flex-1 min-h-0 overflow-y-auto pt-4 space-y-4">
              {!person ? (
                <div className="text-sm text-muted-foreground">Keine Person ausgewählt.</div>
              ) : !hasAccount ? (
                <div className="rounded-md border p-4 text-sm text-muted-foreground">
                  Diese Person hat keinen Login-Zugang. Ein Zugang entsteht bei der Anmeldung/Onboarding
                  oder wenn eine Willkommensmail versendet wird.
                  <div className="mt-3">
                    <Button type="button" variant="outline" onClick={handleResendOnboarding} disabled={accessBusy}>
                      Registrierungsmail senden
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Login</label>
                      <div className="rounded-md border p-3 text-sm font-mono">{person.login || '—'}</div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Login-E-Mail</label>
                      <div className="rounded-md border p-3 text-sm">{person.email || '—'}</div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Zugang</label>
                    <div className="flex items-center justify-between rounded-md border p-3">
                      <div className="text-sm text-muted-foreground">
                        {person.accessLocked
                          ? 'Der Login ist gesperrt — die Person kann sich nicht anmelden.'
                          : 'Der Login ist aktiv.'}
                      </div>
                      <Switch
                        checked={!person.accessLocked}
                        disabled={accessBusy}
                        onCheckedChange={(checked: boolean) => void handleToggleAccess(!checked)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Passwort setzen</label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Neues Passwort (min. 8 Zeichen)"
                      />
                      <Button type="button" onClick={handleSetPassword} disabled={accessBusy || newPassword.length < 8}>
                        Setzen
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Alternativ eine Willkommensmail senden, damit die Person das Passwort selbst vergibt.
                    </div>
                    <div>
                      <Button type="button" variant="outline" onClick={handleResendOnboarding} disabled={accessBusy}>
                        Registrierungsmail senden
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </WizardShell>

      <SubProfileForm
        parent={person as any}
        isOpen={subWizardOpen}
        onClose={() => {
          setSubWizardOpen(false);
          setEditingSubIndex(null);
        }}
        onSave={saveSubprofile}
        initialData={editingSubIndex !== null ? subprofiles[editingSubIndex] : null}
      />

      <ConfirmWizardModal
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Person löschen"
        description={person ? person.name : ''}
        confirmLabel="Löschen"
        onConfirm={handleDeletePerson}
        body={
          <div className="space-y-3">
            <div className="text-sm">
              Möchtest du diese Person wirklich endgültig löschen? Die Akte ist danach nicht mehr
              vorhanden. Soll sie nur aus den aktiven Listen verschwinden, archiviere sie
              stattdessen.
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="person-delete-reason">
                Begründung (wird protokolliert)
              </label>
              <Textarea
                id="person-delete-reason"
                rows={2}
                value={deleteReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDeleteReason(e.target.value)}
              />
            </div>
          </div>
        }
      />

      <LoeschenMitGrundDialog
        offen={profilZuLoeschen !== null}
        titel={`Schülerprofil „${profilZuLoeschen !== null ? String(subprofiles[profilZuLoeschen]?.name || '') : ''}“ löschen?`}
        onAbbrechen={() => setProfilZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (profilZuLoeschen !== null) await subprofilLoeschen(profilZuLoeschen, grund);
        }}
      />
    </>
  );
}


